import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";

export default function RepairPrintPage() {
  const { id } = useParams();
  const [repair, setRepair] = useState(null);
  const [error, setError] = useState(false);
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("kk_token")) {
      setAuthed(false);
      return;
    }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/repairs").then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (found) {
        setRepair(found);
        setTimeout(() => window.print(), 700);
      } else {
        setError(true);
      }
    }).catch(() => setError(true));
  }, [authed, id]);

  if (authed === false) return <Navigate to="/admin/login" replace />;
  if (authed === null) return null;
  if (error) return <div className="p-10 text-center">Reparatia nu a fost gasita.</div>;
  if (!repair) return <div className="p-10 text-center text-neutral-500">Se incarca...</div>;

  const STATUS_RO = {
    received: "Primit",
    in_progress: "In lucru",
    completed: "Finalizat",
    delivered: "Predat",
  };

  const shortId = repair.id.slice(0, 8).toUpperCase();
  const createdDate = new Date(repair.created_at);
  const createdAt = createdDate.toLocaleString("ro-RO");
  const createdDay = createdDate.toLocaleDateString("ro-RO");
  const statusUrl = `${window.location.origin}/status/${shortId.toLowerCase()}`;
  const warrantyCheckUrl = `${window.location.origin}/garantie/${shortId.toLowerCase()}`;
  const hasWarranty = repair.warranty_months > 0 && repair.warranty_until;
  const totalPages = hasWarranty ? 3 : 2;
  const warrantyStart = repair.completed_at ? new Date(repair.completed_at) : new Date();
  const warrantyEnd = hasWarranty ? new Date(repair.warranty_until) : null;
  const fmtDay = (d) => d.toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" });

  const downloadPdf = async () => {
    const html2pdf = (await import("html2pdf.js")).default;
    const el = document.querySelector(".kk-pdf-target");
    if (!el) return;
    await html2pdf().set({
      margin: 8,
      filename: `kukirin-ticket-${shortId}.pdf`,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"] },
    }).from(el).save();
  };

  return (
    <div data-testid="repair-print-page" className="kk-print bg-white text-black min-h-screen">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .kk-page { page-break-after: always; }
          .kk-page:last-child { page-break-after: auto; }
        }
        .kk-print { font-family: 'Outfit', sans-serif; }
        .kk-print h1, .kk-print h2 { font-family: 'Anton', sans-serif; letter-spacing: -0.02em; }
      `}</style>

      <div className="no-print fixed top-4 right-4 flex gap-2 z-50">
        <button data-testid="print-btn" onClick={() => window.print()} className="bg-[#00E5FF] text-black font-semibold px-4 py-2 text-sm uppercase tracking-wider">
          Printeaza
        </button>
        <button data-testid="pdf-btn" onClick={downloadPdf} className="bg-white text-black font-semibold px-4 py-2 text-sm uppercase tracking-wider">
          Salveaza PDF
        </button>
        <button data-testid="close-btn" onClick={() => window.close()} className="border border-neutral-400 text-neutral-700 px-4 py-2 text-sm uppercase tracking-wider">
          Inchide
        </button>
      </div>

      <div className="kk-pdf-target">

      {/* ============ PAGINA 1: FISA TROTINETA ============ */}
      <div className="kk-page max-w-3xl mx-auto p-8">
        <PrintHeader shortId={shortId} status={STATUS_RO[repair.status] || repair.status} />

        <h2 className="text-5xl uppercase font-bold mt-8 leading-none">
          Fisa <span className="text-[#00E5FF]">trotineta</span> in service
        </h2>
        <div className="text-sm text-neutral-600 mt-2">Data primirii: <span className="font-medium text-black">{createdAt}</span></div>

        <Section title="Client">
          <Row label="Nume" value={repair.customer_name} big />
          <Row label="Telefon" value={repair.customer_phone} big />
        </Section>

        <Section title="Trotineta">
          <Row label="Model" value={repair.scooter_model} big />
          <Row label="Numar serie" value={repair.serial_number || "-"} />
        </Section>

        <Section title="Problema raportata">
          <div className="bg-neutral-100 border-l-4 border-[#00E5FF] p-4 text-sm leading-relaxed whitespace-pre-wrap min-h-[120px]">
            {repair.issue_description}
          </div>
        </Section>

        <Section title="Costuri">
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-neutral-300 p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cost estimat</div>
              <div className="text-3xl font-bold mt-1">{repair.estimated_cost ? `${repair.estimated_cost} RON` : "-"}</div>
            </div>
            <div className="border border-neutral-300 p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cost final</div>
              <div className="text-3xl font-bold mt-1 text-[#00E5FF]">{repair.final_cost != null ? `${repair.final_cost} RON` : "-"}</div>
            </div>
          </div>
        </Section>

        <Section title="Note service (de completat la lucrare)">
          <div className="space-y-3 mt-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="border-b border-neutral-400 h-6" />
            ))}
          </div>
        </Section>

        <div className="mt-6 flex items-center gap-4 p-4 bg-neutral-50 border border-neutral-300">
          <div className="bg-white p-2 border border-neutral-200">
            <QRCodeSVG value={statusUrl} size={88} level="M" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-bold">Scaneaza pentru status</div>
            <div className="text-sm font-medium mt-1">Clientul poate vedea statusul live al reparatiei</div>
            <div className="text-[10px] text-neutral-500 mt-1 font-mono">{statusUrl}</div>
          </div>
        </div>

        <div className="mt-6 text-center text-[10px] uppercase tracking-widest text-neutral-500">
          Pagina 1 / {totalPages} · Foaie atelier · Ticket #{shortId}
        </div>
      </div>

      {/* ============ PAGINA 2: CONTRACT / DECLARATIE CLIENT ============ */}
      <div className="kk-page max-w-3xl mx-auto p-8">
        <PrintHeader shortId={shortId} status={STATUS_RO[repair.status] || repair.status} />

        <h2 className="text-4xl uppercase font-bold mt-8 leading-none">
          Contract de <span className="text-[#00E5FF]">primire</span> in service
        </h2>
        <div className="text-sm text-neutral-600 mt-2">
          Incheiat astazi <span className="font-medium text-black">{createdDay}</span> intre:
        </div>

        <div className="grid grid-cols-2 gap-6 mt-5">
          <div className="border border-neutral-300 p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-bold mb-2">Prestator</div>
            <div className="font-bold">TROTI-X SERVICE</div>
            <div className="text-xs text-neutral-700 mt-1 leading-relaxed">
              Bd. Liviu Rebreanu 104A<br />
              Timisoara, Timis<br />
              Tel: 0743 110 277
            </div>
          </div>
          <div className="border border-neutral-300 p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-bold mb-2">Client</div>
            <div className="font-bold">{repair.customer_name}</div>
            <div className="text-xs text-neutral-700 mt-1 leading-relaxed">
              Telefon: {repair.customer_phone}
            </div>
          </div>
        </div>

        <div className="mt-5 border border-neutral-300 p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-bold mb-2">Obiectul contractului</div>
          <div className="text-sm">
            <strong>Trotineta:</strong> {repair.scooter_model}
            {repair.serial_number ? <> · <strong>Serie:</strong> {repair.serial_number}</> : null}
          </div>
          <div className="text-sm mt-1">
            <strong>Problema declarata:</strong> {repair.issue_description}
          </div>
          {repair.estimated_cost ? (
            <div className="text-sm mt-1"><strong>Cost estimat orientativ:</strong> {repair.estimated_cost} RON</div>
          ) : null}
        </div>

        <Section title="Termeni si conditii">
          <ol className="text-[11px] leading-relaxed space-y-1.5 list-decimal pl-5 text-neutral-800">
            <li>Clientul preda spre service trotineta descrisa mai sus, recunoscand ca este proprietarul de drept al acesteia.</li>
            <li>Diagnosticarea trotinetei este <strong>gratuita</strong> la prima vizita. Eventualele costuri suplimentare (piese, manopera) vor fi comunicate clientului spre acceptare inainte de executarea lucrarilor.</li>
            <li>Costul estimat este orientativ. Costul final poate diferi in functie de complexitatea reala a defectiunii si va fi confirmat telefonic / prin SMS inainte de lucrare.</li>
            <li>Termen estimativ de rezolvare: 2-14 zile lucratoare, in functie de disponibilitatea pieselor. Daca apar intarzieri, clientul va fi informat.</li>
            <li>Notificarea finalizarii se face prin <strong>SMS</strong> la numarul de telefon comunicat de client.</li>
            <li><strong>Taxa de stationare:</strong> dupa <strong>7 zile</strong> de la notificarea SMS de finalizare, se percepe o taxa de <strong>10 RON / zi calendaristica</strong> pentru depozitare. Aceasta taxa se cumuleaza la costul reparatiei si se achita la ridicare.</li>
            <li>Dupa <strong>90 de zile</strong> de la notificarea de finalizare, daca trotineta nu este ridicata si nu se achita costurile, prestatorul isi rezerva dreptul de a o considera abandonata, conform legislatiei in vigoare.</li>
            <li>Prestatorul nu raspunde pentru bunurile / accesoriile lasate pe trotineta care nu sunt descrise in fisa (gentute, telefoane, chei, etc). Va rugam sa le luati cu dvs.</li>
            <li>Pentru defectiuni acoperite de garantie (trotinete cumparate de la <strong>trotinete-ieftine.ro</strong>), clientul trebuie sa prezinte factura / dovada cumpararii. Garantia nu acopera uzura normala, accidentele, contactul cu apa peste IPX-ul declarat, modificarile neautorizate.</li>
            <li>Plata se face la ridicare, in numerar sau prin transfer bancar.</li>
            <li>Datele personale (nume, telefon) sunt prelucrate conform <strong>GDPR</strong> exclusiv pentru gestionarea acestei reparatii si comunicarea statusului (SMS). Nu sunt transmise tertilor.</li>
            <li>Clientul declara ca a citit, a inteles si este de acord cu termenii de mai sus.</li>
          </ol>
        </Section>

        <div className="grid grid-cols-2 gap-12 mt-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-12">Client (nume + semnatura)</div>
            <div className="border-t border-black pt-2">
              <div className="font-medium">{repair.customer_name}</div>
              <div className="text-[10px] text-neutral-500">Data: {createdDay}</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-12">Prestator (semnatura + stampila)</div>
            <div className="border-t border-black pt-2">
              <div className="font-medium">TROTI-X SERVICE</div>
              <div className="text-[10px] text-neutral-500">Data: {createdDay}</div>
            </div>
          </div>
        </div>

        <div className="mt-10 text-center text-[10px] uppercase tracking-widest text-neutral-500">
          Pagina 2 / {totalPages} · Exemplar pentru client · Ticket #{shortId}
        </div>
      </div>

      {/* ============ PAGINA 3: CERTIFICAT DE GARANTIE ============ */}
      {hasWarranty && (
        <div data-testid="warranty-certificate" className="kk-page max-w-3xl mx-auto p-8">
          <div className="border-4 border-black p-1">
            <div className="border border-[#00E5FF] p-8 relative">
              {/* colturi decorative */}
              <div className="absolute top-2 left-2 w-8 h-8 border-t-4 border-l-4 border-[#00E5FF]" />
              <div className="absolute top-2 right-2 w-8 h-8 border-t-4 border-r-4 border-[#00E5FF]" />
              <div className="absolute bottom-2 left-2 w-8 h-8 border-b-4 border-l-4 border-[#00E5FF]" />
              <div className="absolute bottom-2 right-2 w-8 h-8 border-b-4 border-r-4 border-[#00E5FF]" />

              <div className="text-center">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 bg-black flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#00E5FF"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></svg>
                  </div>
                  <div className="text-left">
                    <div className="text-xl font-bold uppercase leading-none">TROTI-X SERVICE</div>
                    <div className="text-[9px] uppercase tracking-[0.3em] text-neutral-600 mt-1">Bd. Liviu Rebreanu 104A · Timisoara · 0743 110 277</div>
                  </div>
                </div>
                <div className="mt-6 text-[10px] uppercase tracking-[0.5em] text-neutral-500">// Document oficial //</div>
                <h2 className="text-5xl uppercase font-bold mt-2 leading-none">
                  Certificat de <span className="text-[#00E5FF]">garantie</span>
                </h2>
                <div className="inline-block mt-3 bg-black text-white px-4 py-1.5 text-sm font-mono tracking-widest">
                  Nr. G-{shortId}
                </div>
              </div>

              <div className="mt-8 text-center text-sm text-neutral-700 leading-relaxed max-w-xl mx-auto">
                Prin prezentul certificat, <strong>TROTI-X SERVICE</strong> garanteaza lucrarea de service executata pentru:
              </div>

              <div className="grid grid-cols-2 gap-4 mt-5">
                <div className="border border-neutral-300 p-4 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Client</div>
                  <div className="text-lg font-bold mt-1">{repair.customer_name}</div>
                  <div className="text-xs text-neutral-600">{repair.customer_phone}</div>
                </div>
                <div className="border border-neutral-300 p-4 text-center">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Trotineta</div>
                  <div className="text-lg font-bold mt-1">{repair.scooter_model}</div>
                  <div className="text-xs text-neutral-600">{repair.serial_number ? `Serie: ${repair.serial_number}` : `Ticket #${shortId}`}</div>
                </div>
              </div>

              <div className="mt-4 bg-neutral-50 border-l-4 border-[#00E5FF] p-4">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1">Lucrari / piese acoperite de garantie</div>
                <div className="text-base font-semibold">{repair.warranty_items || repair.completion_notes || "Lucrarea de service executata"}</div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-5 text-center">
                <div className="border-2 border-black p-4">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Perioada</div>
                  <div className="text-4xl font-bold mt-1 leading-none">{repair.warranty_months}</div>
                  <div className="text-[10px] uppercase tracking-widest mt-1 font-bold text-[#00E5FF]">{repair.warranty_months === 1 ? "luna" : "luni"}</div>
                </div>
                <div className="border border-neutral-300 p-4">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500">Valabila de la</div>
                  <div className="text-sm font-bold mt-2">{fmtDay(warrantyStart)}</div>
                </div>
                <div className="border border-neutral-300 p-4 bg-black text-white">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-400">Expira la</div>
                  <div className="text-sm font-bold mt-2 text-[#00E5FF]">{fmtDay(warrantyEnd)}</div>
                </div>
              </div>

              <div className="mt-5 text-[10px] leading-relaxed text-neutral-700 border border-neutral-200 p-3">
                <strong>Conditii:</strong> Garantia acopera exclusiv piesele inlocuite si lucrarile mentionate mai sus, executate de TROTI-X SERVICE. Garantia NU acopera: uzura normala, lovituri/accidente, contactul cu apa peste clasa IP declarata, interventii neautorizate ulterioare, alte componente decat cele mentionate. Pentru solicitari in garantie, prezentati acest certificat impreuna cu trotineta la sediul nostru. Verificare rapida: scanati codul QR de mai jos.
              </div>

              <div className="mt-6 flex items-end justify-between">
                <div className="text-center">
                  <div className="bg-white p-1.5 border border-neutral-300 inline-block">
                    <QRCodeSVG value={warrantyCheckUrl} size={64} level="M" />
                  </div>
                  <div className="text-[8px] uppercase tracking-widest text-neutral-500 mt-1">Verificare garantie</div>
                </div>
                <div className="text-center">
                  <div className="w-28 h-28 rounded-full border-4 border-[#00E5FF] flex flex-col items-center justify-center mx-auto rotate-[-8deg]">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 mb-0.5" fill="#00E5FF"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></svg>
                    <div className="text-[9px] font-bold uppercase tracking-widest leading-tight">Troti-X<br />Service</div>
                    <div className="text-[7px] uppercase tracking-widest text-neutral-500 mt-0.5">Garantat</div>
                  </div>
                </div>
                <div className="text-center w-48">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-10">Semnatura si stampila</div>
                  <div className="border-t border-black pt-1.5">
                    <div className="text-xs font-bold">TROTI-X SERVICE</div>
                    <div className="text-[9px] text-neutral-500">Data: {fmtDay(warrantyStart)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center text-[10px] uppercase tracking-widest text-neutral-500">
            Pagina 3 / {totalPages} · Certificat de garantie · Nr. G-{shortId}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

const PrintHeader = ({ shortId, status }) => (
  <div className="flex items-start justify-between pb-5 border-b-2 border-black">
    <div>
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 bg-[#00E5FF] flex items-center justify-center text-white font-bold text-xl">K</div>
        <div>
          <h1 className="text-2xl font-bold uppercase leading-none">TROTI-X SERVICE</h1>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00E5FF] mt-1">Service oficial - Timisoara</div>
        </div>
      </div>
      <div className="text-xs text-neutral-700 mt-3 leading-relaxed">
        Bd. Liviu Rebreanu 104A, Timisoara, Timis<br />
        Tel: 0743 110 277 · Program: L-V 9:00-17:00, Sambata 9:00-14:00<br />
        Partener garantie: trotinete-ieftine.ro
      </div>
    </div>
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ticket #</div>
      <div className="text-3xl font-bold tracking-tight">{shortId}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">Status</div>
      <div className="inline-block bg-[#00E5FF] text-white px-2 py-1 text-xs uppercase tracking-wider font-bold mt-1">
        {status}
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mt-6">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-2">// {title}</div>
    {children}
  </div>
);

const Row = ({ label, value, big }) => (
  <div className="flex items-baseline gap-4 py-2 border-b border-neutral-200">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 w-32 flex-shrink-0">{label}</div>
    <div className={`font-medium ${big ? "text-xl" : "text-base"}`}>{value}</div>
  </div>
);
